/**
 * viewMutations.ts
 *
 * Atomic mutations for board views within collection_view blocks.
 * Updates both appDB.blocks and appDB.syncQueue in one transaction.
 *
 * IDB RESILIENCE:
 *   All view mutations read the block from IDB to build the optimistic local
 *   state, but the API payload is fully self-contained from the function
 *   arguments. This means every mutation can fall back to the API when IDB
 *   is unavailable or quota-full without needing to read any IDB state first.
 *
 *   Two-layer fallback pattern (same as all other mutation files):
 *     1. Pre-flight check: if IDB unavailable → call API directly.
 *     2. Mid-session quota catch: if write fails → mark quota_full + call API.
 */

import { appDB, emitIDBChange } from "../db/client";
import { refreshPendingCount } from "../stores/syncMetaStore";
import { isIDBAvailable, setIDBStatus, isQuotaError } from "../idbStatus";
import { postWithAuth, deleteWithAuth } from "@/lib/api-helpers";

import type { ViewCollection, View, LayoutSettings, SortItem, BlockSettings } from "@/types/board";
import type {
  ViewUpdatePayload,
  LayoutUpdatePayload,
  PropertyVisibilityUpdatePayload,
  FilterUpdatePayload,
  SortUpdatePayload,
  ColumnWidthsUpdatePayload,
  GroupUpdatePayload,
  ViewAddPayload,
  ViewDeletePayload,
  ChartSettingsUpdatePayload,
  ToggleLockPayload,
  BlockSettingsUpdatePayload,
  AdvancedFilterUpdatePayload,
  DashboardSettingsUpdatePayload,
} from "../db/schema";

// ─── Internal helper ──────────────────────────────────────────────

function handleQuotaError(mutationName: string): void {
  setIDBStatus("quota_full");
  console.warn(
    `%c[${mutationName}] IDB quota exceeded mid-session — falling back to direct API`,
    "color: #fb923c; font-weight: bold;"
  );
}

// ─────────────────────────────────────────────────────────────────
// UPDATE VIEW (rename / icon / viewType)
// Routes to /api/database/update-view-type
// ─────────────────────────────────────────────────────────────────

export interface UpdateViewOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  updates: {
    title?: string;
    icon?: string;
    viewType?: View["viewType"];
    formQuestions?: any[];
    formTitle?: string;
    formDescription?: string;
    formIcon?: string;
    formCoverImage?: string | null;
    formCoverPosition?: { x: number; y: number } | null;
    isPublicForm?: "private" | "public" | "workspace-only";
    formAnonymousResponses?: boolean;
  };
}

export async function updateViewMutation(options: UpdateViewOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, updates } = options;

  const payload: ViewUpdatePayload = { blockId, viewTypeId, workspaceId, ...updates };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/update-view-type", { blockId, viewTypeId, ...updates });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateViewMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      // API uses null to clear the form cover; locally the View type stores undefined.
      const localUpdates: Partial<View> = {
        ...updates,
        ...(updates.formCoverImage === null ? { formCoverImage: undefined } : {}),
        ...(updates.formCoverPosition === null ? { formCoverPosition: undefined } : {}),
      } as Partial<View>;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          return vtId === viewTypeId ? { ...vt, ...localUpdates } : vt;
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "VIEW_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateViewMutation");
      await postWithAuth("/api/database/update-view-type", { blockId, viewTypeId, ...updates });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// REORDER VIEWS
// Routes to /api/database/re-order-views
// ─────────────────────────────────────────────────────────────────

export async function reorderViewsMutation(options: {
  blockId: string;
  order: string[];
  workspaceId: string;
}): Promise<void> {
  const { blockId, order, workspaceId } = options;

  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/re-order-views", { blockId, order });
    return;
  }

  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[reorderViewsMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const viewTypes = value.viewsTypes || [];
      const byId = new Map(viewTypes.filter((vt) => vt._id).map((vt) => [String(vt._id), vt]));
      const reordered: View[] = [];
      for (const id of order) {
        const vt = byId.get(String(id));
        if (vt) {
          reordered.push(vt);
          byId.delete(String(id));
        }
      }
      for (const vt of viewTypes) {
        if (!vt._id || byId.has(String(vt._id))) reordered.push(vt);
      }

      await appDB.blocks.put({
        ...block,
        value: { ...value, viewsTypes: reordered },
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "VIEW_REORDER",
        payload: { blockId, order, workspaceId },
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderViewsMutation");
      await postWithAuth("/api/database/re-order-views", { blockId, order });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE LAYOUT
// Routes to /api/database/settings/layout
// ─────────────────────────────────────────────────────────────────

export interface UpdateLayoutOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  layoutSettings: Partial<LayoutSettings>;
}

export async function updateLayoutMutation(options: UpdateLayoutOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, layoutSettings } = options;

  const payload: LayoutUpdatePayload = { blockId, viewTypeId, workspaceId, layoutSettings };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/layout", { blockId, viewTypeId, layoutSettings });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateLayoutMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return {
            ...vt,
            settings: {
              ...(vt.settings || {}),
              layout: { ...(vt.settings?.layout || {}), ...layoutSettings },
            },
          };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "LAYOUT_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateLayoutMutation");
      await postWithAuth("/api/database/settings/layout", { blockId, viewTypeId, layoutSettings });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE PROPERTY VISIBILITY
// Routes to /api/database/settings/property-visibility
// ─────────────────────────────────────────────────────────────────

export interface UpdatePropertyVisibilityOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  propertyIds: string[];
}

export async function updatePropertyVisibilityMutation(
  options: UpdatePropertyVisibilityOptions
): Promise<void> {
  const { blockId, viewTypeId, workspaceId, propertyIds } = options;

  const propertyVisibility = (propertyIds || []).map((id) => ({ propertyId: id }));
  const payload: PropertyVisibilityUpdatePayload = { blockId, viewTypeId, workspaceId, propertyVisibility };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/property-visibility", { blockId, viewTypeId, propertyVisibility });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updatePropertyVisibilityMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), propertyVisibility } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "PROPERTY_VISIBILITY_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updatePropertyVisibilityMutation");
      await postWithAuth("/api/database/settings/property-visibility", { blockId, viewTypeId, propertyVisibility });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE FILTER
// Routes to /api/database/settings/filter
// ─────────────────────────────────────────────────────────────────

export interface UpdateFilterOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  filters: Record<string, string[]>;
}

export async function updateFilterMutation(options: UpdateFilterOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, filters } = options;

  const filtersArray = Object.entries(filters).map(([propertyId, value]) => ({ propertyId, value }));
  const payload: FilterUpdatePayload = { blockId, viewTypeId, workspaceId, filters: filtersArray };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/filter", { blockId, viewTypeId, filters: filtersArray });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateFilterMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), filters: filtersArray } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "FILTER_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateFilterMutation");
      await postWithAuth("/api/database/settings/filter", { blockId, viewTypeId, filters: filtersArray });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE SORT
// Routes to /api/database/settings/sort
// ─────────────────────────────────────────────────────────────────

export interface UpdateSortOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  sorts: SortItem[];
}

export async function updateSortMutation(options: UpdateSortOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, sorts } = options;

  const sortsArray = (sorts || []).map((s) => ({ propertyId: s.propertyId, direction: s.direction }));
  const payload: SortUpdatePayload = { blockId, viewTypeId, workspaceId, sorts: sortsArray };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/sort", { blockId, viewTypeId, sorts: sortsArray });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateSortMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), sorts: sortsArray } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "SORT_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateSortMutation");
      await postWithAuth("/api/database/settings/sort", { blockId, viewTypeId, sorts: sortsArray });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE COLUMN WIDTHS
// Routes to /api/database/settings/column-widths
// ─────────────────────────────────────────────────────────────────

export interface UpdateColumnWidthsOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  /** Full width map for the view (propertyId → px); replaces the stored map. */
  columnWidths: Record<string, number>;
}

export async function updateColumnWidthsMutation(options: UpdateColumnWidthsOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, columnWidths } = options;

  const widths: Record<string, number> = {};
  for (const [propertyId, px] of Object.entries(columnWidths || {})) {
    if (typeof px === "number" && Number.isFinite(px)) widths[propertyId] = Math.round(px);
  }
  const payload: ColumnWidthsUpdatePayload = { blockId, viewTypeId, workspaceId, columnWidths: widths };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/column-widths", { blockId, viewTypeId, columnWidths: widths });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateColumnWidthsMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), columnWidths: widths } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "COLUMN_WIDTHS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateColumnWidthsMutation");
      await postWithAuth("/api/database/settings/column-widths", { blockId, viewTypeId, columnWidths: widths });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE GROUP
// Routes to /api/database/settings/group
// ─────────────────────────────────────────────────────────────────

export interface UpdateGroupOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  group: {
    propertyId: string;
    sortDirection?: string;
    hideEmptyGroups?: boolean;
    colorColumn?: boolean;
  } | null;
}

export async function updateGroupMutation(options: UpdateGroupOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, group } = options;

  const groupPayload = group
    ? {
        propertyId: group.propertyId,
        sortDirection: group.sortDirection,
        hideEmptyGroups: group.hideEmptyGroups,
        colorColumn: group.colorColumn,
      }
    : null;

  const payload: GroupUpdatePayload = { blockId, viewTypeId, workspaceId, group: groupPayload };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/group", { blockId, viewTypeId, group: groupPayload });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateGroupMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return {
            ...vt,
            settings: {
              ...(vt.settings || {}),
              group: group
                ? {
                    propertyId: group.propertyId,
                    sortDirection: group.sortDirection as any,
                    hideEmptyGroups: group.hideEmptyGroups,
                    colorColumn: group.colorColumn,
                  }
                : undefined,
            },
          };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "GROUP_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateGroupMutation");
      await postWithAuth("/api/database/settings/group", { blockId, viewTypeId, group: groupPayload });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// ADD VIEW
// Routes to /api/database/add-view-type
// ─────────────────────────────────────────────────────────────────

export interface AddViewOptions {
  blockId: string;
  viewId: string;
  typeToAdd: string;
  viewTypeValue: any;
  workspaceId: string;
}

export async function addViewMutation(options: AddViewOptions): Promise<void> {
  const { blockId, viewId, typeToAdd, viewTypeValue, workspaceId } = options;

  const payload: ViewAddPayload = { blockId, viewId, typeToAdd, viewTypeValue, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/add-view-type", { blockId, viewId, typeToAdd, viewTypeValue });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[addViewMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: [...(value.viewsTypes || []), viewTypeValue],
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "VIEW_ADD",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addViewMutation");
      await postWithAuth("/api/database/add-view-type", { blockId, viewId, typeToAdd, viewTypeValue });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE VIEW
// Routes to DELETE /api/database/delete-view-type
// ─────────────────────────────────────────────────────────────────

export interface DeleteViewOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
}

export async function deleteViewMutation(options: DeleteViewOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId } = options;

  const payload: ViewDeletePayload = { blockId, viewTypeId, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await deleteWithAuth("/api/database/delete-view-type", {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ blockId, viewTypeToDelete: viewTypeId }),
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[deleteViewMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const viewToDelete = (value.viewsTypes || []).find(
        (vt) => (vt._id ? String(vt._id) : "") === viewTypeId,
      );
      if (
        viewToDelete &&
        !viewToDelete.pinnedToDashboard &&
        (value.viewsTypes || []).filter((vt) => !vt.pinnedToDashboard).length <= 1
      ) {
        throw new Error("[deleteViewMutation] Cannot delete the last view of a database");
      }
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).filter((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          return vtId !== viewTypeId;
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "VIEW_DELETE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("deleteViewMutation");
      await deleteWithAuth("/api/database/delete-view-type", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blockId, viewTypeToDelete: viewTypeId }),
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE CHART SETTINGS
// Routes to /api/database/settings/chart
// ─────────────────────────────────────────────────────────────────

export interface UpdateChartSettingsOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  chartSettings: any;
}

export async function updateChartSettingsMutation(options: UpdateChartSettingsOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, chartSettings } = options;

  const payload: ChartSettingsUpdatePayload = { blockId, viewTypeId, workspaceId, chartSettings };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/chart", { blockId, viewTypeId, chartSettings });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateChartSettingsMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), chart: chartSettings } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "CHART_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateChartSettingsMutation");
      await postWithAuth("/api/database/settings/chart", { blockId, viewTypeId, chartSettings });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE BLOCK SETTINGS
// Routes to /api/database/settings/block
// ─────────────────────────────────────────────────────────────────

export interface UpdateBlockSettingsOptions {
  blockId: string;
  workspaceId: string;
  settings: Partial<BlockSettings>;
}

export async function updateBlockSettingsMutation(options: UpdateBlockSettingsOptions): Promise<void> {
  const { blockId, workspaceId, settings } = options;

  const payload: BlockSettingsUpdatePayload = { blockId, workspaceId, settings };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/block", { blockId, settings });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateBlockSettingsMutation] Block ${blockId} not found or not a collection_view`);
      }

      // Block-level settings (the whole collection view), not per-view.
      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        settings: { ...(value.settings || {}), ...settings },
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateBlockSettingsMutation");
      await postWithAuth("/api/database/settings/block", { blockId, settings });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// TOGGLE LOCK
// Routes to /api/database/settings/lock
// ─────────────────────────────────────────────────────────────────

export interface ToggleLockOptions {
  blockId: string;
  workspaceId: string;
  isLocked: boolean;
}

export async function toggleLockMutation(options: ToggleLockOptions): Promise<void> {
  const { blockId, workspaceId, isLocked } = options;

  const payload: ToggleLockPayload = { blockId, workspaceId, isLocked };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/lock", { blockId, isLocked });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[toggleLockMutation] Block ${blockId} not found or not a collection_view`);
      }

      // Block-level lock (the whole collection view), not per-view.
      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = { ...value, isLocked };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      // Mirror the backend cascade: lock the default datasource locally too.
      const defaultDataSourceId =
        value.defaultDataSourceId ||
        (value.viewsTypes || []).find((vt) => !vt.pinnedToDashboard)?.databaseSourceId ||
        (value.viewsTypes || [])[0]?.databaseSourceId;
      if (defaultDataSourceId) {
        const dsId = String(defaultDataSourceId);
        const existingDs = await appDB.dataSources.get(dsId);
        if (existingDs) {
          await appDB.dataSources.update(dsId, { isLocked, localUpdatedAt: Date.now() });
        }
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "TOGGLE_LOCK",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("toggleLockMutation");
      await postWithAuth("/api/database/settings/lock", { blockId, isLocked });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE ADVANCED FILTERS
// Routes to /api/database/settings/filter (different body shape from FILTER_UPDATE)
// ─────────────────────────────────────────────────────────────────

export interface UpdateAdvancedFilterOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  advancedFilters: any[];
}

export async function updateAdvancedFilterMutation(options: UpdateAdvancedFilterOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, advancedFilters } = options;

  const payload: AdvancedFilterUpdatePayload = { blockId, viewTypeId, workspaceId, advancedFilters };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/filter", { blockId, viewTypeId, advancedFilters });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateAdvancedFilterMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      const updatedValue: ViewCollection = {
        ...value,
        viewsTypes: (value.viewsTypes || []).map((vt) => {
          const vtId = vt._id ? String(vt._id) : "";
          if (vtId !== viewTypeId) return vt;
          return { ...vt, settings: { ...(vt.settings || {}), advancedFilters } };
        }),
      };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "ADVANCED_FILTER_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateAdvancedFilterMutation");
      await postWithAuth("/api/database/settings/filter", { blockId, viewTypeId, advancedFilters });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DASHBOARD SETTINGS UPDATE
// Routes to /api/database/settings/dashboard. Handles dashboard layout
// (rows / chrome) and atomic widget create/delete in one shot.
// ─────────────────────────────────────────────────────────────────

export interface UpdateDashboardSettingsOptions {
  blockId: string;
  viewTypeId: string;
  workspaceId: string;
  dashboard: any;
  createWidget?: { view: any; targetRowId: string };
  deleteWidget?: { viewId: string };
}

export async function updateDashboardSettingsMutation(options: UpdateDashboardSettingsOptions): Promise<void> {
  const { blockId, viewTypeId, workspaceId, dashboard, createWidget, deleteWidget } = options;

  const payload: DashboardSettingsUpdatePayload = {
    blockId,
    viewTypeId,
    workspaceId,
    dashboard,
    ...(createWidget ? { createWidget } : {}),
    ...(deleteWidget ? { deleteWidget } : {}),
  };

  // Direct API fallback when IDB unavailable.
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/settings/dashboard", payload);
    return;
  }

  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const block = await appDB.blocks.get(blockId);
      if (!block || block.blockType !== "collection_view") {
        throw new Error(`[updateDashboardSettingsMutation] Block ${blockId} not found or not a collection_view`);
      }

      const value = block.value as ViewCollection;
      let nextViewsTypes: View[] = value.viewsTypes || [];

      // Atomic widget delete — strip from viewsTypes[].
      if (deleteWidget) {
        nextViewsTypes = nextViewsTypes.filter((v) => String(v._id) !== String(deleteWidget.viewId));
      }

      // Atomic widget create — append to viewsTypes[].
      if (createWidget) {
        nextViewsTypes = [...nextViewsTypes, createWidget.view as View];
      }

      // Replace the dashboard view's settings.dashboard with the new layout.
      nextViewsTypes = nextViewsTypes.map((v) => {
        if (String(v._id) !== viewTypeId) return v;
        return { ...v, settings: { ...(v.settings || {}), dashboard } };
      });

      const updatedValue: ViewCollection = { ...value, viewsTypes: nextViewsTypes };

      await appDB.blocks.put({ ...block, value: updatedValue, isSync: 0 as 0, localUpdatedAt: Date.now() });

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DASHBOARD_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await refreshPendingCount();
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateDashboardSettingsMutation");
      await postWithAuth("/api/database/settings/dashboard", payload);
      return;
    }
    throw err;
  }
}
